import { baseFetch } from "../../helpers/apiClient";

/**
 * What the member may do, from the server rather than from a stored role string.
 *
 * ## What this replaces
 *
 * Authorization currently reads `localStorage.role`, a string built by
 * `profileRes.role.join("_")` and matched with `String.includes`. A dual-role
 * member is literally `"mentee_mentor"`, and `"mentee_mentor".includes("mentor")`
 * is how the app decides what they may do.
 *
 * Two problems with that, beyond the obvious. It is substring matching standing
 * in for authorization, so any capability name containing another matches both.
 * And it is client-owned: local storage is editable, so the string is a claim
 * the member makes about themselves.
 *
 * The contract is server-derived and loaded per request, so a revoked capability
 * takes effect immediately rather than when a token expires.
 *
 * ## Client-side guards are UX, not security
 *
 * The server enforces. These helpers exist so a member is not shown a control
 * that will fail, and must never stand in for a real check.
 */

/**
 * The persisted capability vocabulary, mirroring the server.
 *
 * Kept in step with `core/constants/capability.constants.js` in Echo-Backend.
 * `platformAccess` is the one that replaced this app's `allowedRoles` checks:
 * every member holds it and an admin does not, which is precisely what the
 * three-role list was expressing.
 */
export const CAPABILITIES = Object.freeze({
  platformAccess: "platform:access",
  matchingParticipate: "matching:participate",
  mentorshipReceive: "mentorship:receive",
  mentorshipProvide: "mentorship:provide",
  eventsManage: "events:manage",
  orgManage: "org:manage",
  retentionView: "retention:view",
});

/** Badge identifiers the contract can return. */
export const BADGES = Object.freeze({
  emailVerified: "email_verified",
  mentorVerified: "mentor_verified",
  organizationVerified: "organization_verified",
});

/** A contract that permits nothing. What every failure path returns. */
const EMPTY = Object.freeze({ capabilities: [], badges: [] });

const asArray = (value) => (Array.isArray(value) ? value : []);

/** The capabilities held, tolerating a malformed or absent contract. */
export const capabilitiesFromContract = (contract) =>
  asArray(contract?.capabilities);

/**
 * Whether the member holds a capability.
 *
 * Exact match, never substring: `"mentorship:receive".includes("mentorship:")`
 * is true, and matching that way is the bug this replaces.
 *
 * Fails closed. A member whose contract has not loaded may do nothing, rather
 * than everything.
 */
export const hasCapability = (contract, capability) =>
  capabilitiesFromContract(contract).includes(capability);

/** Whether the member has earned a badge. Unknown badges are not earned. */
export const hasBadge = (contract, type) =>
  asArray(contract?.badges).some(
    (badge) => badge.type === type && badge.earned === true,
  );

/**
 * Load the contract.
 *
 * Never throws and never rejects: a guarded route that cannot load the contract
 * should render as "may do nothing", not crash. The caller cannot tell an empty
 * contract from a failed one, which is deliberate — both mean the same thing to
 * a guard.
 */
export const fetchAccountContract = async () => {
  try {
    const response = await baseFetch({ url: "/v2/me/capabilities" });
    const data = response?.data ?? response;
    return {
      capabilities: asArray(data?.capabilities),
      badges: asArray(data?.badges),
    };
  } catch {
    return { ...EMPTY };
  }
};

/**
 * The legacy `localStorage.role` string, derived from the contract.
 *
 * A compatibility shim, and deliberately so. 41 reads of that string remain
 * across dashboards, profile editors and navigation, and they are not
 * authorization: `roleProfile()` turns the string into a *data key* —
 * `menteeProfile`, `mentorProfile`, `menteeMentorProfile`, `sponsorProfile` —
 * that selects which sub-document of the profile response to read and write.
 * Rewriting those as capability checks would model the wrong thing. The real
 * fix is for the API to stop returning role-keyed sub-documents (ECH-374).
 *
 * What this does fix is that the string had three writers and no owner: login
 * and `App` both computed `role.join("_")`, and onboarding completion hardcoded
 * `"mentee"`. A member who chose "provide mentorship" holds `["mentee","mentor"]`
 * on the server and read as `"mentee"` here, so `roleProfile` handed them
 * `menteeProfile` and they saw the wrong dashboard until they signed out and
 * back in. Now there is one writer, and it derives from what the server says.
 *
 * Returns `""` rather than guessing when the contract permits nothing —
 * guessing a role is exactly what caused the bug.
 */
export const legacyRoleString = (contract) => {
  const holds = (capability) => hasCapability(contract, capability);

  // Before mentorship: a sponsor also holds `retention:view`, and checking that
  // first would label them an admin.
  if (holds(CAPABILITIES.orgManage)) return "sponsor";

  const receives = holds(CAPABILITIES.mentorshipReceive);
  const provides = holds(CAPABILITIES.mentorshipProvide);
  if (receives && provides) return "mentee_mentor";
  if (provides) return "mentor";
  if (receives) return "mentee";

  if (holds(CAPABILITIES.retentionView)) return "admin";
  return "";
};
